import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Card,
  Grid,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
  useTheme,
} from "@mui/material";
import { useUser } from "../contexts/UserContext";

const accentColor = "#DF7468";
const bgColor = "#f7f9fa";
const cardBg = "#fff";
const borderRadius = 10;

const font = {
  heading: {
    fontFamily: "Poppins, Merriweather, serif",
    fontWeight: 700,
    letterSpacing: "-0.5px",
  },
  subheading: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
  },
  body: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
  },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    jobTitle: "",
    email: "",
    phoneNumber: "",
    agreement: false,
  });
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStart = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formValues.name,
          jobTitle: formValues.jobTitle,
          email: formValues.email,
          phoneNumber: formValues.phoneNumber,
        }),
      });
      const data = await response.json();
      if (data && data.id) {
        await fetch(`${process.env.REACT_APP_API_URL}/send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formValues.email }),
        });
        setPendingUserId(data.id);
        setPendingEmail(formValues.email);
        setOtpDialogOpen(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        alert("Failed to create user. Please try again.");
      }
    } catch {
      setIsLoading(false);
      alert("Error creating user. Please try again.");
    }
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    setOtpError("");
  };

  const handleOtpSubmit = async () => {
    setIsLoading(true);
    setOtpError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail, otp }),
      });
      const result = await res.json();
      if (result.success) {
        setUser({
          id: pendingUserId,
          name: formValues.name,
          email: pendingEmail,
        });
        setOtpDialogOpen(false);
        navigate(`/dashboard/${pendingUserId}`);
      } else {
        setOtpError(result.message || "Invalid OTP. Please try again.");
      }
    } catch {
      setOtpError("Error verifying OTP. Please try again.");
    }
    setIsLoading(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bgColor }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography sx={{ ...font.subheading, color: "#fff" }}>
          Loading your Dashboard...
        </Typography>
      </Backdrop>
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: cardBg, borderBottom: "1px solid #eee" }}
      >
        <Container maxWidth="md">
          <Toolbar
            disableGutters
            sx={{ justifyContent: "space-between", minHeight: 72 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => navigate("/")}
              tabIndex={0}
              aria-label="Go to home"
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && navigate("/")
              }
            >
              <Typography
                component="span"
                sx={{
                  ...font.heading,
                  fontSize: { xs: 24, md: 28 },
                  color: "#222",
                }}
              >
                Coach Carol
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate("/login")}
              sx={{
                borderRadius: 24,
                px: 3,
                py: 1,
                borderColor: "#222",
                color: "#222",
                ...font.body,
                fontSize: 15,
                minWidth: 90,
                "&:hover": { borderColor: accentColor, color: accentColor },
              }}
            >
              Login
            </Button>
          </Toolbar>
        </Container>
      </AppBar>
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 6 } }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius,
            bgcolor: cardBg,
            boxShadow: "0 2px 16px 0 rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: { xs: "center", sm: "space-between" },
              alignItems: "center",
              mb: 3,
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <img
              src="/images/Logo/cai logo.jpg"
              alt="Career Agility International logo"
              style={{
                maxHeight: 38,
                maxWidth: "45vw",
                width: "auto",
                objectFit: "contain",
                borderRadius: 2,
                display: "block",
              }}
              tabIndex={0}
              aria-label="Career Agility International logo"
            />
            {/* <img
              src="/assets/subsea7logo.png"
              alt="Subsea7 logo"
              style={{
                maxHeight: 32,
                maxWidth: "45vw",
                width: "auto",
                objectFit: "contain",
                display: "block",
              }}
              tabIndex={0}
              aria-label="Subsea7 logo"
            /> */}
          </Box>
          <Typography
            variant="h4"
            align="center"
            sx={{
              ...font.heading,
              color: "#222",
              mb: 1,
              fontSize: { xs: 26, md: 32 },
            }}
          >
            Welcome to your Career Support Program
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{
              ...font.body,
              color: "#666",
              mb: 3,
              fontSize: { xs: 16, md: 18 },
            }}
          >
            Access Coach Carol and exclusive job search resources. Start by
            entering your details below.
          </Typography>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            sx={{ width: "100%", mb: 2 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="outlined"
                  autoComplete="name"
                  inputProps={{ "aria-label": "Name" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  name="jobTitle"
                  value={formValues.jobTitle}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="outlined"
                  autoComplete="organization-title"
                  inputProps={{ "aria-label": "Job Title" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Personal Email"
                  name="email"
                  type="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="outlined"
                  autoComplete="email"
                  inputProps={{ "aria-label": "Personal Email" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Personal Mobile"
                  name="phoneNumber"
                  type="tel"
                  value={formValues.phoneNumber}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  variant="outlined"
                  autoComplete="tel"
                  inputProps={{ "aria-label": "Personal Mobile" }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreement"
                      checked={formValues.agreement}
                      onChange={handleInputChange}
                      color="primary"
                      sx={{ p: 0.5 }}
                      inputProps={{ "aria-label": "Agreement" }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ ...font.body, fontSize: 14, color: "#444" }}
                    >
                      I agree to take up the CareerCare Programme by Career
                      Agility International.
                    </Typography>
                  }
                  sx={{ alignItems: "flex-start", mt: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={
              !formValues.agreement ||
              !formValues.name ||
              !formValues.jobTitle ||
              !formValues.email ||
              !formValues.phoneNumber
            }
            sx={{
              py: 1.5,
              ...font.subheading,
              fontSize: 17,
              borderRadius: 8,
              bgcolor: accentColor,
              color: "#fff",
              width: { xs: "100%", sm: "auto" },
              px: { sm: 8 },
              mt: 2,
              boxShadow: "none",
              "&:hover": { bgcolor: "#C86254" },
              transition: "background 0.2s",
            }}
            aria-label="Start Career Support Program"
          >
            Let's Get Started
          </Button>
          <Typography
            variant="body2"
            align="center"
            sx={{
              ...font.body,
              color: "#888",
              mt: 4,
              fontSize: 13,
              maxWidth: 420,
            }}
          >
            <b>Statement of Confidentiality:</b> All coaching sessions provided
            by Career Agility International Pte Ltd Career Mentors will be kept
            strictly confidential. Unless with written or verbal consent, Career
            Agility International Pte Ltd will not release any information, in
            part or in whole, to another company and/or any third party
            regarding your personal and private data.
          </Typography>
        </Card>
      </Container>
      <Dialog open={otpDialogOpen} disableEscapeKeyDown>
        <DialogTitle sx={{ ...font.heading, fontSize: 20, color: "#222" }}>
          Verify Your Email
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, ...font.body, color: "#444" }}>
            An OTP has been sent to <b>{pendingEmail}</b>. Please enter it below
            to verify your email.
          </Typography>
          <TextField
            label="OTP"
            value={otp}
            onChange={handleOtpChange}
            fullWidth
            autoFocus
            inputProps={{
              maxLength: 6,
              inputMode: "numeric",
              "aria-label": "OTP",
            }}
            disabled={isLoading}
            sx={{ mb: 1 }}
          />
          {otpError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {otpError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleOtpSubmit}
            variant="contained"
            disabled={isLoading || otp.length !== 6}
            sx={{
              bgcolor: accentColor,
              color: "#fff",
              borderRadius: 8,
              boxShadow: "none",
              "&:hover": { bgcolor: "#C86254" },
              fontWeight: 500,
              fontSize: 16,
              px: 4,
            }}
            aria-label="Verify OTP"
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
